import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Snackbar,
  Alert,
  CircularProgress,
  Card,
  CardContent
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';

const GOLD = '#d4af37';

interface NewsOverviewContent {
  pageTitle: string;
  pageSubtitle: string;
  introText: string;
  newsHighlight: string;
  eventsHighlight: string;
  announcementsHighlight: string;
  subscriptionTitle: string;
  subscriptionText: string;
}

export default function NewsOverviewManager() {
  const [content, setContent] = useState<NewsOverviewContent>({
    pageTitle: 'Stay Connected with Temple Updates',
    pageSubtitle: 'Get the latest news, events, and announcements from Muchukunnu Temple',
    introText: 'Welcome to our News & Events section. Stay informed about temple activities, upcoming festivals, and important announcements.',
    newsHighlight: 'Daily activities, priest schedules, and general news from the temple administration.',
    eventsHighlight: 'Festival calendars, special poojas, and community programs with dates and essential details.',
    announcementsHighlight: 'Official communications, circulars, and day-to-day notices for devotees and staff.',
    subscriptionTitle: '📧 Subscribe to Updates',
    subscriptionText: 'Never miss an update! Subscribe to receive notifications about important temple news, upcoming events, and special announcements directly to your email.'
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    try {
      setLoading(true);
      console.log('Loading news overview content from Firebase...');
      const docRef = doc(db, 'news_overview_content', 'main');
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data() as NewsOverviewContent;
        setContent(data);
        console.log('Loaded news overview content:', data);
      } else {
        console.log('No content found, using defaults');
      }
    } catch (error) {
      console.error('Error loading content:', error);
      setSnackbar({ open: true, message: 'Error loading content', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      console.log('Saving news overview content to Firebase...');
      const docRef = doc(db, 'news_overview_content', 'main');
      await setDoc(docRef, content);
      console.log('Content saved successfully');
      setSnackbar({ open: true, message: 'Content saved successfully!', severity: 'success' });
    } catch (error) {
      console.error('Error saving content:', error);
      setSnackbar({ open: true, message: 'Error saving content', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field: keyof NewsOverviewContent, value: string) => {
    setContent(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress sx={{ color: GOLD }} />
      </Box>
    );
  }

  return (
    <Box>
      <Paper elevation={3} sx={{ p: 4, mb: 3, background: `linear-gradient(135deg, ${GOLD}15 0%, #fff 100%)` }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#2c3e50', mb: 1 }}>
              News Overview Page Manager
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Edit the content that appears on the News Overview landing page
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadContent}
              sx={{ borderColor: GOLD, color: GOLD, '&:hover': { borderColor: GOLD, bgcolor: `${GOLD}10` } }}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving}
              sx={{ 
                bgcolor: GOLD, 
                color: '#000',
                fontWeight: 700,
                '&:hover': { bgcolor: '#c49b2e' }
              }}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        </Box>
      </Paper>

      <Grid container spacing={3}>
        {/* Page Header Section */}
        <Grid item xs={12}>
          <Card sx={{ border: `2px solid ${GOLD}` }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 700, color: GOLD, mb: 3 }}>
                📰 Page Header
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Page Title"
                    value={content.pageTitle}
                    onChange={(e) => handleChange('pageTitle', e.target.value)}
                    variant="outlined"
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Page Subtitle"
                    value={content.pageSubtitle}
                    onChange={(e) => handleChange('pageSubtitle', e.target.value)}
                    variant="outlined"
                    multiline
                    rows={2}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Introduction Section */}
        <Grid item xs={12}>
          <Card sx={{ border: `2px solid ${GOLD}` }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 700, color: GOLD, mb: 3 }}>
                📝 Introduction Text
              </Typography>
              <TextField
                fullWidth
                label="Intro Paragraph"
                value={content.introText}
                onChange={(e) => handleChange('introText', e.target.value)}
                variant="outlined"
                multiline
                rows={3}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Highlights Section */}
        <Grid item xs={12}>
          <Card sx={{ border: `2px solid ${GOLD}` }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 700, color: GOLD, mb: 3 }}>
                ✨ Section Highlights
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#2196f3' }}>
                    📰 News Highlight
                  </Typography>
                  <TextField
                    fullWidth
                    value={content.newsHighlight}
                    onChange={(e) => handleChange('newsHighlight', e.target.value)}
                    variant="outlined"
                    multiline
                    rows={2}
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#4caf50' }}>
                    🎉 Events Highlight
                  </Typography>
                  <TextField
                    fullWidth
                    value={content.eventsHighlight}
                    onChange={(e) => handleChange('eventsHighlight', e.target.value)}
                    variant="outlined"
                    multiline
                    rows={2}
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#ff9800' }}>
                    📢 Announcements Highlight
                  </Typography>
                  <TextField
                    fullWidth
                    value={content.announcementsHighlight}
                    onChange={(e) => handleChange('announcementsHighlight', e.target.value)}
                    variant="outlined"
                    multiline
                    rows={2}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Subscription Section */}
        <Grid item xs={12}>
          <Card sx={{ border: `2px solid ${GOLD}` }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 700, color: GOLD, mb: 3 }}>
                📧 Email Subscription Section
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Subscription Title"
                    value={content.subscriptionTitle}
                    onChange={(e) => handleChange('subscriptionTitle', e.target.value)}
                    variant="outlined"
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Subscription Description"
                    value={content.subscriptionText}
                    onChange={(e) => handleChange('subscriptionText', e.target.value)}
                    variant="outlined"
                    multiline
                    rows={3}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
